import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { FiAlertCircle, FiCheck } from 'react-icons/fi';
import api from '../services/api';
import { redirectToGoogleOAuth } from '../services/oauth';
import GoogleIcon from '../components/GoogleIcon';
import PaperboyWordmark from '../components/PaperboyWordmark';
import { TOPICS } from '../constants/topics';
import './AuthPages.css';

const STEPS = ['Name', 'Topics', 'Cadence', 'Ready'];
const DELIVERY_TIMES = ['05:30', '06:00', '06:30', '07:00', '07:30', '08:00'];
const FREQUENCIES = ['Daily', 'Weekly'];

const StepIndicator = ({ current }) => (
  <div className="step-indicator" aria-label={`Step ${current + 1} of ${STEPS.length}`}>
    {STEPS.map((label, i) => {
      const isDone = i < current;
      const isActive = i === current;
      const circleClass = isDone
        ? 'step-indicator__circle step-indicator__circle--done'
        : isActive
        ? 'step-indicator__circle step-indicator__circle--active'
        : 'step-indicator__circle step-indicator__circle--future';
      const labelClass = isDone
        ? 'step-indicator__label step-indicator__label--done'
        : isActive
        ? 'step-indicator__label step-indicator__label--active'
        : 'step-indicator__label';

      return (
        <React.Fragment key={label}>
          <div className="step-indicator__item">
            <div className={circleClass} aria-hidden="true">
              {isDone ? '✓' : i + 1}
            </div>
            <span className={labelClass}>{label}</span>
          </div>
          {i < STEPS.length - 1 && (
            <span className={`step-indicator__connector${isDone ? ' step-indicator__connector--done' : ''}`} aria-hidden="true" />
          )}
        </React.Fragment>
      );
    })}
  </div>
);

const validatePassword = (pwd) => ({
  length: pwd.length >= 8,
  uppercase: /[A-Z]/.test(pwd),
  lowercase: /[a-z]/.test(pwd),
  number: /[0-9]/.test(pwd),
});

const RegisterPage = () => {
  const [step, setStep] = useState(0);
  const [name, setName] = useState('');
  const [pickedTopics, setPickedTopics] = useState([]);
  const [cadence, setCadence] = useState('Daily');
  const [deliveryTime, setDeliveryTime] = useState('06:30');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [localError, setLocalError] = useState('');

  const { register, error, clearError, isAuthenticated } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/', { replace: true });
    }
  }, [isAuthenticated, navigate]);

  useEffect(() => {
    return () => clearError();
  }, [clearError]);

  useEffect(() => {
    if (localError || error) {
      const timer = setTimeout(() => {
        setLocalError('');
        clearError();
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [localError, error, clearError]);

  const toggleTopic = (id) => {
    setPickedTopics((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const passwordChecks = validatePassword(password);
  const isPasswordValid = Object.values(passwordChecks).every(Boolean);
  const isEmailValid = email.length > 0 && /\S+@\S+\.\S+/.test(email);

  const canAdvance = [
    name.trim().length > 0,
    pickedTopics.length > 0,
    deliveryTime.length > 0,
    isEmailValid && isPasswordValid,
  ][step];

  const totalCount = pickedTopics.reduce(
    (sum, id) => sum + (TOPICS.find((t) => t.id === id)?.count || 0),
    0
  );

  const handleSubmit = async () => {
    if (!isEmailValid) {
      setLocalError('Please enter a valid email address');
      return;
    }
    if (!isPasswordValid) {
      setLocalError('Password does not meet requirements');
      return;
    }

    setIsLoading(true);
    setLocalError('');

    const result = await register(email, password, name);

    if (result.success) {
      try {
        await api.put('/user/preferences', {
          favorite_tags: pickedTopics,
          digest_frequency: cadence.toLowerCase(),
          digest_time: deliveryTime,
        });
      } catch (prefErr) {
        console.warn('Preferences save failed, continuing:', prefErr);
      }
      setIsLoading(false);
      navigate('/', { replace: true });
      return;
    }

    setIsLoading(false);
  };

  const displayError = localError || error;

  return (
    <div className="auth-page auth-page--register">
      <div className="auth-shell-header">
        <Link to="/"><PaperboyWordmark size={20} /></Link>
        <StepIndicator current={step} />
        <div className="auth-shell-skip">
          <Link to="/">Skip ↗</Link>
        </div>
      </div>

      <div className="auth-shell-body">
        {step === 0 && (
          <div className="auth-step">
            <span className="mono-tag mono-tag--accent">STEP 01 / 04</span>
            <h1 className="heading-xl">First — what should we call you?</h1>
            <p className="subtitle">We use it on the masthead. (And nowhere else, really.)</p>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="input-name"
              maxLength={100}
              placeholder="Your name"
              autoFocus
              autoComplete="name"
            />
          </div>
        )}

        {step === 1 && (
          <div className="auth-step auth-step--wide">
            <span className="mono-tag mono-tag--accent">STEP 02 / 04</span>
            <h1 className="heading-xl">What do you want on your front page, {name}?</h1>
            <p className="subtitle">Pick a few. The more specific, the better.</p>
            <div className="chips-grid">
              {TOPICS.map((topic) => {
                const active = pickedTopics.includes(topic.id);
                return (
                  <button
                    key={topic.id}
                    className={`chip${active ? ' chip--active' : ''}`}
                    onClick={() => toggleTopic(topic.id)}
                    type="button"
                    aria-pressed={active}
                  >
                    {active && <span className="accent-dot" aria-hidden="true" />}
                    {topic.label}
                    <span className="chip__count">{topic.count}</span>
                  </button>
                );
              })}
            </div>
            <div className="summary-bar">
              <span className="summary-bar__picked">● {pickedTopics.length} TOPICS PICKED</span>
              <span className="summary-bar__sep">·</span>
              <span>Estimated read: ~{Math.max(6, pickedTopics.length * 2)} min/day</span>
              <span className="summary-bar__sep">·</span>
              <span>{totalCount.toLocaleString()} stories ranked daily</span>
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="auth-step">
            <span className="mono-tag mono-tag--accent">STEP 03 / 04</span>
            <h1 className="heading-lg">When should we drop the paper?</h1>
            <p className="subtitle">
              You'll get one front page a {cadence.toLowerCase()}. Pick a time.
            </p>

            <div className="cadence-section">
              <span className="mono-tag cadence-section__label">FREQUENCY</span>
              <div className="cadence-chips">
                {FREQUENCIES.map((f) => (
                  <button
                    key={f}
                    className={`chip${cadence === f ? ' chip--active' : ''}`}
                    onClick={() => setCadence(f)}
                    type="button"
                    aria-pressed={cadence === f}
                  >
                    {cadence === f && <span className="accent-dot" aria-hidden="true" />}
                    {f}
                  </button>
                ))}
              </div>
            </div>

            <div className="cadence-section">
              <span className="mono-tag cadence-section__label">DELIVERY TIME</span>
              <div className="cadence-chips">
                {DELIVERY_TIMES.map((t) => (
                  <button
                    key={t}
                    className={`time-chip${deliveryTime === t ? ' time-chip--active' : ''}`}
                    onClick={() => setDeliveryTime(t)}
                    type="button"
                    aria-pressed={deliveryTime === t}
                  >
                    {t}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="auth-step">
            <span className="mono-tag mono-tag--accent">STEP 04 / 04 · ALMOST</span>
            <h2 className="heading-welcome">
              Welcome, <span className="heading-welcome__name">{name}</span>.<br />
              Your paper drops at {deliveryTime}.
            </h2>
            <p className="subtitle">
              One last thing — give us an email and a password to lock down your front page.
            </p>

            {displayError && (
              <div className="auth-error" role="alert">
                <FiAlertCircle aria-hidden="true" />
                <span>{displayError}</span>
              </div>
            )}

            <div className="summary-card">
              <span className="summary-card__title">YOUR SETUP</span>
              <p className="summary-card__line">
                <strong>{name}</strong> · {pickedTopics.length} topics · {deliveryTime} delivery · {cadence}
              </p>
              <p className="summary-card__topics">
                {pickedTopics
                  .map((id) => TOPICS.find((t) => t.id === id)?.label)
                  .join(' · ')
                  .toUpperCase()}
              </p>
            </div>

            <div className="auth-form auth-form--no-mb">
              <div className="form-field">
                <label htmlFor="email" className="form-label">Email</label>
                <input
                  type="email"
                  id="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="input-hairline"
                  placeholder="you@example.com"
                  disabled={isLoading}
                  autoComplete="email"
                  required
                />
              </div>

              <div className="form-field">
                <label htmlFor="password" className="form-label">Password</label>
                <input
                  type="password"
                  id="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="input-hairline"
                  placeholder="Create a password"
                  disabled={isLoading}
                  autoComplete="new-password"
                  required
                />
                {password && (
                  <div className="password-requirements">
                    <div className={`requirement${passwordChecks.length ? ' met' : ''}`}>
                      <FiCheck aria-hidden="true" /> 8+ characters
                    </div>
                    <div className={`requirement${passwordChecks.uppercase ? ' met' : ''}`}>
                      <FiCheck aria-hidden="true" /> Uppercase
                    </div>
                    <div className={`requirement${passwordChecks.lowercase ? ' met' : ''}`}>
                      <FiCheck aria-hidden="true" /> Lowercase
                    </div>
                    <div className={`requirement${passwordChecks.number ? ' met' : ''}`}>
                      <FiCheck aria-hidden="true" /> Number
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="divider-or">
              <span className="divider-or__label">OR</span>
            </div>

            <button
              type="button"
              className="btn-google"
              disabled={isLoading}
              onClick={redirectToGoogleOAuth}
            >
              <GoogleIcon />
              Sign up with Google
            </button>
          </div>
        )}
      </div>

      <div className="auth-shell-footer">
        <button
          type="button"
          className="btn-ghost"
          onClick={() => step === 0 ? navigate('/') : setStep((s) => s - 1)}
        >
          {step === 0 ? '← Back to home' : '← Back'}
        </button>

        <span className="auth-shell-footer__counter">{step + 1} of {STEPS.length}</span>

        {step < 3 ? (
          <button
            type="button"
            className="btn-ink"
            onClick={() => setStep((s) => s + 1)}
            disabled={!canAdvance}
          >
            Next →
            <span className="accent-dot" aria-hidden="true" />
          </button>
        ) : (
          <button
            type="button"
            className="btn-ink"
            onClick={handleSubmit}
            disabled={isLoading || !canAdvance}
          >
            {isLoading ? (
              <>
                <span className="spinner" aria-hidden="true" />
                Creating...
              </>
            ) : (
              <>
                Create my front page →
                <span className="accent-dot" aria-hidden="true" />
              </>
            )}
          </button>
        )}
      </div>

      <div className="register-auth-footer">
        <p>Already have an account? <Link to="/login">Sign in</Link></p>
      </div>
    </div>
  );
};

export default RegisterPage;
